import { useSceneEvents } from "@/components/common/scene-events-context";
import type { WayPointTarget } from "@/components/r3f/renderers/odometry-paths/walk-paths-renderer";
import type { WaypointPosition } from "@/components/r3f/renderers/waypoint-label-render";
import { useAppStore } from "@/store/store-hooks";
import type {
  ActiveToolAndPicking,
  PickingToolsRef,
} from "@/tools/picking-tools";
import { useTypedEvent } from "@faro-lotv/app-component-toolbox";
import type {
  GUID,
  IElementGenericAnnotation,
  IElementGenericImgSheet,
  IElementImageCommand,
  IElementImg360,
} from "@faro-lotv/ielement-types";
import type { CachedWorldTransform } from "@faro-lotv/project-source";
import type { Vector3 as Vector3Prop } from "@react-three/fiber";
import { useState, type JSX } from "react";
import type { Box3, Plane, Quaternion, Vector3 } from "three";
import { WalkSceneAnimation } from "./walk-scene-animation";
import { WalkSceneEventsPostman } from "./walk-scene-events-postman";
import { WalkSceneStatic } from "./walk-scene-static";
import {
  ViewType,
  type WalkSceneActiveElement,
  type WalkSceneOverlayElement,
} from "./walk-types";

export type WalkSceneCoreProps = PickingToolsRef & {
  /** The current element of the scene */
  currentElement: WalkSceneActiveElement;

  /** The element we want to switch to */
  targetElement?: WalkSceneActiveElement;

  /** The 2D photo object (image command) to look at */
  photoObject?: IElementImageCommand;

  /** Element that should be highlighted, e.g. with a hover effect */
  highlightedElement?: GUID;

  /** floor plan where the placeholders are placed */
  sheetForElevation?: IElementGenericImgSheet;

  /** The element that we want to render together with the current element */
  overlayElement?: WalkSceneOverlayElement;

  /** Which view is this: main view in walk mode, left view, or right view in splitscreen mode */
  viewType: ViewType;

  /** List of all the placeholders to show */
  placeholders: IElementImg360[];

  /** If true other panorama's placeholders will be rendered  */
  shouldShowPlaceholders: boolean;

  /** if provided quaternion should be applied while performing pano animation */
  targetQuaternion?: Quaternion;

  /** List of all the annotations for this area */
  annotations: IElementGenericAnnotation[];

  /** Describe the active tool and whether it requires picking */
  activeToolAndPicking: ActiveToolAndPicking;

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /** Whether the 3d models should be rendered on demand */
  renderOnDemand: boolean;

  /** Callback executed when the animation finishes */
  onAnimationFinished(active: WalkSceneActiveElement): void;

  /** Signal that the scene changed the camera position by animating the camera to a new pano */
  onCameraMovedViaAnimation?(position: Vector3Prop): void;

  /** Callback executed, when the rendered transform of the pano changed */
  onPanoTransformChanged(transform: CachedWorldTransform): void;

  /**
   * Callback executed when the user walks to a different position,
   * by clicking on the floor of the point cloud or by clicking on a measurement icon
   */
  onUserWalkedTo(p: Vector3): void;

  /** Callback executed when the active element should be changed */
  onTargetElementChanged(element: WalkSceneActiveElement): void;

  /** Callback called when the active waypoint target element inside the walk scene has changed. */
  onWayPointChanged(target: WayPointTarget): void;

  /** Callback called when the list of displayed waypoints has changed. */
  onRenderedWaypointsChanged(newWaypoints: WaypointPosition[]): void;

  /** Optional callback to signal outside that the bounding box of all models has changed. */
  onAllModelsBoxChanged?(box: Box3): void;
};

/** @returns The rendering logic of the Walk mode */
export function WalkSceneCore({
  currentElement,
  targetElement,
  photoObject,
  overlayElement,
  highlightedElement,
  viewType,
  placeholders,
  shouldShowPlaceholders,
  targetQuaternion,
  annotations,
  sheetForElevation,
  activeToolAndPicking,
  clippingPlanes,
  renderOnDemand,
  onAnimationFinished,
  onUserWalkedTo,
  onCameraMovedViaAnimation,
  onPanoTransformChanged,
  onTargetElementChanged,
  onAllModelsBoxChanged,
  onWayPointChanged,
  onRenderedWaypointsChanged,
  ...toolEvents
}: WalkSceneCoreProps): JSX.Element | null {
  const { lookAt, setCameraView } = useSceneEvents();

  const [eventsPostman] = useState(new WalkSceneEventsPostman());

  const { getState } = useAppStore();

  useTypedEvent(lookAt, (position) => {
    const { activeElement } = getState().selections;
    if (!!activeElement && currentElement.id !== activeElement) {
      eventsPostman.push({
        targetElementId: activeElement,
        lookAt: position.clone(),
      });
    }
  });
  useTypedEvent(setCameraView, (cameraView) => {
    const { activeElement } = getState().selections;
    if (!!activeElement && currentElement.id !== activeElement) {
      eventsPostman.push({
        targetElementId: activeElement,
        cameraView,
      });
    }
  });

  if (targetElement) {
    /** If the target element is defined, we render an animation */
    return (
      <WalkSceneAnimation
        currentElement={currentElement}
        targetElement={targetElement}
        targetQuaternion={targetQuaternion}
        sheetForElevation={sheetForElevation}
        viewType={viewType}
        eventsPostman={eventsPostman}
        onAnimationFinished={onAnimationFinished}
        onCameraMoved={onCameraMovedViaAnimation}
      />
    );
  }
  /** If the target element is not defined, we render a static scene */
  return (
    <WalkSceneStatic
      currentElement={currentElement}
      overlayElement={overlayElement}
      photoObject={photoObject}
      highlightedElement={highlightedElement}
      sheetForElevation={sheetForElevation}
      viewType={viewType}
      placeholders={placeholders}
      shouldShowPlaceholders={shouldShowPlaceholders}
      activeToolAndPicking={activeToolAndPicking}
      annotations={annotations}
      clippingPlanes={clippingPlanes}
      renderOnDemand={renderOnDemand}
      onUserWalkedTo={onUserWalkedTo}
      onTargetElementChanged={onTargetElementChanged}
      onAllModelsBoxChanged={onAllModelsBoxChanged}
      onWayPointChanged={onWayPointChanged}
      onRenderedWaypointsChanged={onRenderedWaypointsChanged}
      onPanoTransformChanged={onPanoTransformChanged}
      {...toolEvents}
    />
  );
}
